import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { FilePlus2, Trash2 } from 'lucide-react';
import LogPropertyQuoteSheet from './LogPropertyQuoteSheet.jsx';
import { getPropertyQuotes, deleteQuote } from '@/services/quotes.service.js';
import { toDate } from '@/lib/date-display.js';

// PROPERTY-LINE-SPEC P2 — the property deal's entered market responses.
// One row per market: carrier, premium + TRIA split, term, key terms line,
// status. Launches LogPropertyQuoteSheet. Self-hides nothing: an empty state
// with the log button IS the surface (the product gap this ships is entering
// what comes back from shopping by email). Compare + select is P4a.
// No em dashes anywhere (CLAUDE.md hard rule).

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return null;
  return `$${v.toLocaleString('en-US')}`;
}

function fmtDate(ymd) {
  if (!ymd) return '';
  const d = toDate(ymd);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// One-line terms summary: the deltas a broker scans for.
function termsLine(terms) {
  if (!terms) return '';
  const bits = [];
  const lim = terms.limits || {};
  const dd = terms.deductibles || {};
  if (money(lim.any_jobsite)) bits.push(`jobsite ${money(lim.any_jobsite)}`);
  if (dd.any_jobsite != null && dd.any_jobsite !== '') bits.push(`ded ${typeof dd.any_jobsite === 'number' ? money(dd.any_jobsite) : dd.any_jobsite}`);
  if (dd.windstorm != null && dd.windstorm !== '') bits.push(`wind/hail ${typeof dd.windstorm === 'number' ? money(dd.windstorm) : dd.windstorm}`);
  if (terms.coinsurance) bits.push(`coins ${terms.coinsurance}`);
  if (terms.special_form) bits.push('special form');
  if (terms.permission_to_occupy) bits.push(`occupy: ${terms.permission_to_occupy.toLowerCase()}`);
  return bits.join(' · ');
}

const STATUS_VARIANT = { selected: 'success', bound: 'success', declined: 'outline', expired: 'outline' };

export default function BrMarketQuotesPanel({ dealId }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed load is not an empty book (David 2026-08-12, tenth instance of
  // the empty-versus-failed class): the panel says it failed instead of
  // rendering the "no responses yet" empty state over an error.
  const [loadFailed, setLoadFailed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [removeId, setRemoveId] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await getPropertyQuotes(dealId);
      setQuotes(Array.isArray(res?.quotes) ? res.quotes : []);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  async function confirmRemove(id) {
    if (removing) return;
    setRemoving(true);
    try {
      await deleteQuote(id);
      setRemoveId(null);
      toast.success('Market quote removed');
      await load();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Remove failed');
    }
    setRemoving(false);
  }

  return (
    <>
      <div className="rounded-card border border-border bg-surface p-3 shadow-sm">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          Market Quotes
        </h3>

        {loading ? (
          <p className="mb-2 text-xs text-text-dim">Loading…</p>
        ) : loadFailed ? (
          <p className="mb-2 text-xs text-warning">
            Market quotes could not be loaded — this is a read failure, not an empty book.{' '}
            <button type="button" className="underline" onClick={() => { setLoading(true); load(); }}>Retry</button>
          </p>
        ) : quotes.length === 0 ? (
          <p className="mb-2 text-xs text-text-dim">
            No market responses entered yet. Log each carrier reply here as it comes back.
          </p>
        ) : (
          <div className="mb-2 flex flex-col gap-1.5">
            {quotes.map((q) => (
              <div key={q.id} className="rounded-card border border-border p-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-xs font-medium text-text">{q.carrier || 'Carrier not recorded'}</span>
                      {q.quote_number ? <span className="text-[10px] text-text-dim">#{q.quote_number}</span> : null}
                      {q.status && q.status !== 'received' ? (
                        <Badge variant={STATUS_VARIANT[q.status] || 'outline'} className="text-[9px]">{q.status}</Badge>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-[11px] text-text-muted">
                      {money(q.premium?.total)}
                      {q.premium?.tria > 0 ? ` (premium ${money(q.premium.base)} + TRIA ${money(q.premium.tria)})` : ''}
                      {q.effective_date ? ` · eff ${fmtDate(q.effective_date)}` : ''}
                      {q.expiration_date ? ` to ${fmtDate(q.expiration_date)}` : ''}
                    </div>
                    {termsLine(q.terms) ? (
                      <div className="mt-0.5 truncate text-[10px] text-text-dim" title={termsLine(q.terms)}>{termsLine(q.terms)}</div>
                    ) : null}
                    {q.subject_to ? (
                      <div className="mt-0.5 truncate text-[10px] text-text-dim" title={q.subject_to}>Subject to: {q.subject_to}</div>
                    ) : null}
                  </div>
                  {removeId === q.id ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => confirmRemove(q.id)} disabled={removing}>
                        {removing ? 'Removing…' : 'Confirm'}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setRemoveId(null)}>
                        Keep
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="shrink-0 text-text-dim hover:text-danger"
                      title="Remove this entry (typo recovery; the carrier reply email is still the source)"
                      onClick={() => setRemoveId(q.id)}
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          size="sm"
          variant="outline"
          className="w-full justify-start gap-2 border-border-strong hover:border-accent hover:bg-accent-soft hover:text-accent"
          onClick={() => { if (dealId) setSheetOpen(true); }}
          title="Enter a carrier's builders risk quote from their reply. One entry per market response."
        >
          <FilePlus2 size={14} strokeWidth={1.5} />
          <span className="text-xs">Log market quote</span>
        </Button>
      </div>

      <LogPropertyQuoteSheet
        dealId={dealId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onSaved={load}
      />
    </>
  );
}
